/**
 * Phase 7b — IT storefront rewrite layer.
 *
 * Content in product_translations / category_translations / ui_translations
 * was originally authored for the CH storefront (Swiss context: TWINT,
 * "Svizzera", "BigFollow.ch"). On sf='it' we render the SAME content but
 * strip Swiss-only references. This is a pure, mechanical last-mile
 * rewrite applied AFTER seo_overrides / DB overlays — so admins can still
 * override any string exactly.
 *
 * HARD RULES:
 *   • Called ONLY when storefront === 'it'. CH (incl. /it/ second locale)
 *     and AT are never touched.
 *   • Longest patterns first so "Svizzera con TWINT" is consumed before
 *     the shorter "Svizzera" / "TWINT" rules could shred it.
 *   • Case-preserving on the first letter for word-shaped tokens.
 *   • CHF symbol/amount rules intentionally EXCLUDED — the 5 known
 *     placeholders are loyalty amounts computed in CHF (see chat
 *     analysis); a naive symbol swap would mis-label EUR/CHF economics.
 *   • Never mutate empty / non-string input.
 */

type Rule = { pattern: RegExp; replace: string | ((m: string) => string) };

// Longest / most specific first.
const RULES: Rule[] = [
  // ---- Product configurator labels (Phase 7 – Produkt-Konfigurator) ----
  // Longest / most specific first so "Anzahl Follower" is caught before
  // the generic "Anzahl" fallback below.
  { pattern: /\bAnzahl Follower\b/gi, replace: 'Numero di follower' },
  { pattern: /\bAnzahl Abonnenten\b/gi, replace: 'Numero di iscritti' },
  { pattern: /\bAnzahl Subscriber\b/gi, replace: 'Numero di iscritti' },
  { pattern: /\bAnzahl Likes\b/gi, replace: 'Numero di mi piace' },
  { pattern: /\bAnzahl Views\b/gi, replace: 'Numero di visualizzazioni' },
  { pattern: /\bAnzahl Aufrufe\b/gi, replace: 'Numero di visualizzazioni' },
  { pattern: /\bAnzahl Plays\b/gi, replace: 'Numero di riproduzioni' },
  { pattern: /\bAnzahl Streams\b/gi, replace: 'Numero di stream' },
  { pattern: /\bAnzahl Hörer\b/gi, replace: 'Numero di ascoltatori' },
  { pattern: /\bAnzahl Mitglieder\b/gi, replace: 'Numero di membri' },
  { pattern: /\bAnzahl Kommentare\b/gi, replace: 'Numero di commenti' },
  { pattern: /\bAnzahl Reposts\b/gi, replace: 'Numero di repost' },
  { pattern: /\bAnzahl Saves\b/gi, replace: 'Numero di salvataggi' },
  { pattern: /\bAnzahl Reaktionen\b/gi, replace: 'Numero di reazioni' },
  { pattern: /\bAnzahl Shares\b/gi, replace: 'Numero di condivisioni' },
  { pattern: /\bAnzahl\b/g, replace: 'Quantità' },
  { pattern: /\bArt der Follower\b/gi, replace: 'Tipo di follower' },
  { pattern: /\bArt der Kommentare\b/gi, replace: 'Tipo di commenti' },
  { pattern: /\bHerkunft der Streams\b/gi, replace: 'Provenienza degli stream' },
  { pattern: /\bHerkunft Hörer\b/gi, replace: 'Provenienza degli ascoltatori' },
  { pattern: /\bPaket\b/g, replace: 'Pacchetto' },

  // ---- Social-proof labels (defense-in-depth; ui_translations override first) ----
  { pattern: /\bZuletzt bestellt\b/g, replace: 'Ultimo ordine' },
  { pattern: /\bBestellungen\b/g, replace: 'ordini' },

  // ---- Type-selector recommended badge & switch prompt ----
  { pattern: /\bEmpfohlen\b/g, replace: 'Consigliato' },
  { pattern: /\bwechseln →/g, replace: 'passa a →' },

  // Compound phrases
  { pattern: /\bSvizzera con TWINT\b/gi, replace: '' },
  { pattern: /\bin der ganzen Schweiz\b/gi, replace: 'in tutta Italia' },
  { pattern: /\bder ganzen Schweiz\b/gi, replace: 'tutta Italia' },
  { pattern: /\bin tutta la Svizzera\b/gi, replace: 'in tutta Italia' },
  { pattern: /\btutta la Svizzera\b/gi, replace: 'tutta Italia' },
  { pattern: /\bmigliori prezzi della Svizzera\b/gi, replace: 'migliori prezzi in Italia' },
  { pattern: /\bBest prices in Switzerland\b/gi, replace: 'Best prices in Italy' },

  // Domain first (more specific than the brand rule below)
  { pattern: /\bbigfollow\.ch\b/g, replace: 'bigfollow.it' },
  { pattern: /\bBigFollow\.ch\b/g, replace: 'BigFollow' },
  { pattern: /\bBIGFOLLOW\.CH\b/g, replace: 'BIGFOLLOW' },

  // Payment method (TWINT is CH-only). Only drops the token itself; the
  // surrounding sentence structure ("Paga con TWINT" → "Paga con") is left
  // to admins/overrides — checkout.twint_desc & payment_methods are not
  // shown on IT anyway (Stripe-only).
  { pattern: /\s*[–—-]?\s*TWINT\s*[,/]?\s*/gi, replace: ' ' },

  // Country nouns (do LAST — shortest, greedy)
  { pattern: /\bSvizzera\b/g, replace: 'Italia' },
  { pattern: /\bsvizzera\b/g, replace: 'italiana' },
  { pattern: /\bsvizzero\b/g, replace: 'italiano' },
  { pattern: /\bSchweiz\b/g, replace: 'Italien' },
  { pattern: /\bSwitzerland\b/g, replace: 'Italy' },
  { pattern: /\bSuisse\b/g, replace: 'Italie' },
];

// Cleanup: collapse whitespace, tidy pipe separators left by removals.
const CLEANUP: Rule[] = [
  { pattern: /\s{2,}/g, replace: ' ' },
  { pattern: /\s+\|/g, replace: ' |' },
  { pattern: /\|\s*\|/g, replace: '|' },
  { pattern: /^\s*\|\s*/, replace: '' },
  { pattern: /\s*\|\s*$/, replace: '' },
  { pattern: /\s+([,.;:!?])/g, replace: '$1' },
  { pattern: /^\s+|\s+$/g, replace: '' },
];

export function rewriteForIT(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') return input ?? '';
  let out = input;
  for (const r of RULES) out = out.replace(r.pattern, r.replace as any);
  for (const r of CLEANUP) out = out.replace(r.pattern, r.replace as any);
  return out;
}

/** Recursively rewrite string leaves of an overlay/product object. */
/**
 * Keys whose values are technical (URLs, IDs, slugs, filenames, prices,
 * icon names, colors, timestamps) and MUST NEVER be rewritten — otherwise
 * a filename like "…-Schweiz-Twint-Bigfollow.ch_.png" gets mangled into a
 * 404 URL. Match is done on the key's suffix/substring, case-insensitive.
 */
const SKIP_KEY_RE =
  /(^|_)(id|slug|url|src|href|path|icon|image_url|image|thumbnail|avatar|media|video|audio|file|filename|price|amount|currency|color|hex|token|hash|uuid|created_at|updated_at|_at|_by)($|_)/i;

/** Value-shape guard: never rewrite anything that looks like a URL/filename. */
function looksLikeUrlOrAsset(s: string): boolean {
  if (s.length > 4096) return false;
  if (/^(https?:)?\/\//i.test(s)) return true;
  if (/^data:/i.test(s)) return true;
  if (/\/storage\/v1\//.test(s)) return true;
  // filename with extension (no whitespace)
  if (!/\s/.test(s) && /\.(png|jpe?g|gif|webp|svg|avif|mp4|webm|mp3|pdf|ico|woff2?)$/i.test(s)) return true;
  return false;
}

export function rewriteObjectForIT<T>(obj: T): T {
  if (obj == null) return obj;
  if (typeof obj === 'string') {
    return (looksLikeUrlOrAsset(obj) ? obj : rewriteForIT(obj)) as unknown as T;
  }
  if (Array.isArray(obj)) return obj.map(rewriteObjectForIT) as unknown as T;
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (typeof v === 'string' && SKIP_KEY_RE.test(k)) {
        out[k] = v;
      } else {
        out[k] = rewriteObjectForIT(v);
      }
    }
    return out as T;
  }
  return obj;
}